import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Card thumbnail with a terminal-flavoured loading skeleton and graceful
 * fallback. Arweave gateways can 404 individual hashes, so we
 *   1. route every URL through wsrv.nl — a free public image proxy that
 *      CDN-caches and resizes on the fly;
 *   2. if the proxied URL fails, try the origin URL directly;
 *   3. if that still fails, walk through any `fallbackSrcs` that the
 *      backend has stashed from slab.cash's `imageUrls` array (front/back
 *      of a slab), each also via proxy-then-origin.
 *
 * Zero extra network cost on the happy path: we only trigger the
 * fallback chain when an <img> emits `onError`.
 */
export function CardImage({
  src,
  fallbackSrcs,
  alt,
  className = "",
  size = 320,
}: {
  src: string;
  fallbackSrcs?: string[];
  alt: string;
  className?: string;
  /** Target width in pixels for the proxied thumbnail. */
  size?: number;
}) {
  // Ordered candidate list: primary first, then any backups. For each
  // URL we try proxied first, then origin — so a given URL gets two
  // attempts before we move to the next candidate.
  const candidates = useMemo(() => {
    const urls = [src, ...(fallbackSrcs ?? [])].filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const u of urls) {
      if (!seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    }
    return out;
  }, [src, fallbackSrcs]);

  const [idx, setIdx] = useState(0);
  const [triedOrigin, setTriedOrigin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // Reset state when `src` changes (same <img> element re-used by React).
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) {
      prevSrc.current = src;
      setIdx(0);
      setTriedOrigin(false);
      setLoaded(false);
      setErrored(false);
    }
  }, [src]);

  if (errored || candidates.length === 0) {
    return (
      <div
        className={
          "flex h-full w-full items-center justify-center bg-bg text-center " +
          className
        }
      >
        <div className="space-y-1 px-2">
          <div className="text-accent text-2xl font-mono">?</div>
          <div className="text-[9px] text-muted font-mono truncate">
            image unavailable
          </div>
        </div>
      </div>
    );
  }

  const current = candidates[idx];
  const imgSrc = triedOrigin ? current : toProxiedUrl(current, size);

  return (
    <div className={"relative h-full w-full " + className}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg">
          <div className="h-8 w-8 rounded-full border-2 border-accent/30 border-t-accent animate-radar" />
        </div>
      )}
      <img
        // key forces React to recreate the <img> element on each retry,
        // which bypasses the browser's cached-failure state for the URL.
        key={`${idx}-${triedOrigin ? "origin" : "proxy"}`}
        src={imgSrc}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (!triedOrigin) {
            // Proxy failed — swap to origin URL for this candidate.
            setTriedOrigin(true);
            return;
          }
          // Origin failed too — advance to next candidate, or give up.
          if (idx + 1 < candidates.length) {
            setIdx(idx + 1);
            setTriedOrigin(false);
          } else {
            setErrored(true);
          }
        }}
        className={
          "h-full w-full object-contain transition-opacity duration-300 " +
          (loaded ? "opacity-100" : "opacity-0")
        }
      />
    </div>
  );
}

/** Route image through wsrv.nl for CDN caching + resize. Falls through
 *  for data: / blob: URLs. */
function toProxiedUrl(src: string, width: number): string {
  if (!src) return src;
  if (src.startsWith("data:") || src.startsWith("blob:")) return src;
  const encoded = encodeURIComponent(src);
  return `https://wsrv.nl/?url=${encoded}&w=${width}&output=webp&q=80&we`;
}
